import { useEffect, type MutableRefObject } from "react";
import { twMerge } from "tailwind-merge";
import { motion, AnimatePresence } from "motion/react";
import { Trash2 } from "lucide-react";
import { useFieldArray, FieldErrors } from "react-hook-form";
import { z } from "zod";
import { useForm, type FormState } from "@core/features/form";
import { Form } from "@themes/pollinator/ui/Form";
import { Input } from "@themes/pollinator/ui/Input";
import { Label } from "@themes/pollinator/ui/Label";
import { FieldError } from "@themes/pollinator/ui/FieldError";
import { ActionButtonsGroup } from "@themes/pollinator/ui/ActionButtonsGroup";
import { Button } from "@themes/pollinator/ui/Button";
import { Type } from "@themes/pollinator/ui/Type";
import {
  invitationsSchema,
  type InvitationsFormData,
  type InvitationItem,
} from "@core/features/invitation";

// Helper to safely get item errors from the field array
const getItemErrors = (
  errors: FieldErrors<InvitationsFormData>,
  index: number
): FieldErrors<InvitationItem> | undefined => {
  const itemsErrors = errors.items;
  if (Array.isArray(itemsErrors)) {
    return itemsErrors[index];
  }
  return undefined;
};

export type Props = {
  isLoading: boolean;
  onSubmit: (data: InvitationsFormData) => Promise<void>;
  onCancel?: () => void;
  // Customization props
  emailPlaceholder?: string;
  firstNamePlaceholder?: string;
  lastNamePlaceholder?: string;
  submitButtonText?: string;
  cancelButtonText?: string;
  /** Number of initial empty rows to render (default: 5) */
  initialRowCount?: number;
  /** Whether to show action buttons (defaults to true) */
  showActionButtons?: boolean;
  /** Callback when form state changes (for parent validation tracking) */
  onStateChange?: (state: FormState) => void;
  /** Ref to expose the submit handler to parent */
  submitRef?: MutableRefObject<(() => void) | null>;
  /** Custom schema with limit validation (optional, uses default if not provided) */
  schema?: z.ZodSchema<InvitationsFormData>;
  /** Number of remaining invitation slots (null = unlimited) */
  remainingSlots?: number | null;
  /** Number of invitations already sent (for display when unlimited) */
  usedCount?: number;
  /** Whether to show the usage hint (default: true) */
  showUsageHint?: boolean;
};

export function InvitationForm({
  isLoading,
  onSubmit,
  onCancel,
  emailPlaceholder = "friend@example.com",
  firstNamePlaceholder = "First name",
  lastNamePlaceholder = "Last name",
  submitButtonText = "Send Invites",
  cancelButtonText = "Cancel",
  initialRowCount = 5,
  showActionButtons = true,
  onStateChange,
  submitRef,
  schema,
  remainingSlots,
  usedCount,
  showUsageHint = true,
}: Props) {
  const { form, handleSubmit } = useForm({
    schema: schema ?? invitationsSchema,
    defaultValues: {
      items: Array.from({ length: initialRowCount }, () => ({
        email: "",
        firstName: "",
        lastName: "",
      })),
    },
    onSubmit: async (data) => {
      await onSubmit(data);
      form.reset();
    },
  });

  const {
    control,
    register,
    formState: { isDirty, isValid, isSubmitting, errors },
    getValues,
    trigger,
    reset,
  } = form;

  // Expose submit handler to parent via ref
  useEffect(() => {
    if (submitRef) {
      submitRef.current = handleSubmit;
    }
  }, [submitRef, handleSubmit]);

  // Report form state changes to parent
  useEffect(() => {
    onStateChange?.({ isDirty, isValid, isSubmitting });
  }, [isDirty, isValid, isSubmitting, onStateChange]);

  const { fields, append, remove } = useFieldArray({
    control,
    name: "items",
  });

  const handleBlur = async (index: number) => {
    if (index === fields.length - 1) {
      const valid = await trigger(`items.${index}.email`);
      const email = getValues(`items.${index}.email`);
      if (valid && email.trim()) {
        append({ email: "", firstName: "", lastName: "" });
      }
    }
  };

  const handleCancel = () => {
    reset();
    onCancel?.();
  };

  const renderUsageHint = () => {
    // Unlimited: show sent count with unlimited label
    if (remainingSlots === null && usedCount !== undefined) {
      return (
        <Type variant="caption">
          Unlimited ({usedCount} sent)
        </Type>
      );
    }
    // Limited: show remaining slots with sent count
    if (remainingSlots !== null && remainingSlots !== undefined) {
      const sentCount = usedCount ?? 0;
      return (
        <Type
          variant="caption"
          className={remainingSlots === 0 ? "text-error-600" : undefined}
        >
          {remainingSlots === 0
            ? `No invitation slots remaining (${sentCount} sent)`
            : `${remainingSlots} remaining (${sentCount} sent)`}
        </Type>
      );
    }
    return null;
  };

  return (
    <div className="flex flex-col gap-4 w-full">
      <Form form={form} onSubmit={() => handleSubmit()}>
        <AnimatePresence initial={false}>
          {fields.map((field, index) => (
            <motion.div
              key={field.id}
              layout
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 10 }}
              transition={{ duration: 0.15 }}
              className="flex flex-col gap-1 w-full"
            >
              <div className="flex gap-2 items-end w-full">
                {/* First Name */}
                <div className="flex flex-col gap-1 w-1/4">
                  <Input
                    {...register(`items.${index}.firstName`)}
                    id={`items.${index}.firstName`}
                    placeholder={firstNamePlaceholder}
                    aria-labelledby={`items-${index}-firstName-label`}
                  />
                </div>

                {/* Last Name */}
                <div className="flex flex-col gap-1 w-1/4">
                  <Input
                    {...register(`items.${index}.lastName`)}
                    id={`items.${index}.lastName`}
                    placeholder={lastNamePlaceholder}
                    aria-labelledby={`items-${index}-lastName-label`}
                  />
                </div>

                {/* Email */}
                <div className="flex flex-col gap-1 flex-1">
                  <Input
                    {...register(`items.${index}.email`, {
                      onBlur: () => handleBlur(index),
                    })}
                    id={`items.${index}.email`}
                    type="email"
                    placeholder={emailPlaceholder}
                    error={!!getItemErrors(errors, index)?.email}
                    aria-labelledby={`items-${index}-email-label`}
                    aria-describedby={
                      getItemErrors(errors, index)?.email
                        ? `items-${index}-email-error`
                        : undefined
                    }
                  />
                </div>

                {/* Delete button */}
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => remove(index)}
                  iconComponent={Trash2}
                  className={twMerge(
                    "mb-1",
                    index === 0 && fields.length === 1 && "hidden",
                    index === 0 && fields.length > 1 && "opacity-0"
                  )}
                  aria-label="Remove invitee"
                />
              </div>
              {getItemErrors(errors, index)?.email && (
                <FieldError
                  id={`items-${index}-email-error`}
                  message={getItemErrors(errors, index)?.email?.message}
                />
              )}
            </motion.div>
          ))}
        </AnimatePresence>
        {errors.items?.root && (
          <FieldError
            id="items-root-error"
            message={errors.items.root.message as string | undefined}
          />
        )}
      </Form>
      {showUsageHint && renderUsageHint()}
      {showActionButtons && (
        <ActionButtonsGroup
          primaryButtonText={submitButtonText}
          onPrimaryClick={handleSubmit}
          secondaryButtonText={isDirty ? cancelButtonText : undefined}
          onSecondaryClick={isDirty ? handleCancel : undefined}
          className="size-max w-fit self-end"
          primaryButtonProps={{
            disabled: !isDirty || isSubmitting || isLoading,
            visuallyDisabled: isDirty && !isSubmitting && !isLoading && !isValid,
          }}
        />
      )}
    </div>
  );
}

export default InvitationForm;

